/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormGroup,
  Box,
  Grid,
} from '@mui/material';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import CSVFormatErrorMessage from '../../component/CSVFormatErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {CSVUploadButton} from '../../component/FileUploadButton';
//API import
import {patchDailySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {updateScheduleCSVData} from '../../functions/checkScheduleCSVData';
import {dailyScheduleDataInitial} from './ScheduleTemplate';

/**
 * アップロードされたファイルのスケジュールデータを
 * 選択した特定日のスケジュールへコピーするダイアログ
 * @param props props
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog ダイアログ表示フラグ 設定関数
 * - scheduleChangeHandler 特定日スケジュール更新関数
 * - targetGroupID コピーするグループのID
 * - targetGroupName コピーするグループの名前
 * @returns JSX
 */
const UploadFileDialog = (props: {
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  scheduleChangeHandler: () => void;
  targetGroupID: string;
  targetGroupName: string;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  /** 読み込んだファイルのCSVデータ */
  const [csvData, setCSVData] = useState<string>('');
  /** チェックボックスで選択したコピー先の特定日タイプ */
  const [checkTemplate, setCheckTemplate] = useState<
    (keyof apiIF.res.getDailySchedule)[]
  >([]);
  /** CSVフォーマットエラー */
  const [csvFormatError, setCSVFormatError] = useState({
    errorCode: '',
    parameter: [''],
  });

  //フラグ
  const [diffButtonDisabled, setDiffButtonDisabled] = useState(true); //UPLOADボタン非表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  /** APIエラー時エラーメッセージ表示 */
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  /** CSVフォーマットエラー時エラーメッセージ表示 */
  const [isOpenCsvFormatErrorDialog, setIsOpenCsvFormatErrorDialog] =
    useState(false);

  //特定日タイプ項目
  const tempType: {
    name: keyof apiIF.res.getDailySchedule;
    label: string;
  }[] = [
    {
      name: 'weekday-schedule',
      label: trans('UploadFileDialog.checkBoxLabel.weekday'),
    },
    {
      name: 'holiday-schedule',
      label: trans('UploadFileDialog.checkBoxLabel.holiday'),
    },
    {
      name: 'special-day-1-schedule',
      label: trans('UploadFileDialog.checkBoxLabel.specialDay1'),
    },
    {
      name: 'special-day-2-schedule',
      label: trans('UploadFileDialog.checkBoxLabel.specialDay2'),
    },
  ];

  /**
   * チェックボックスの特定日変更時処理
   * @param event
   */
  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetName = event.target.name as keyof apiIF.res.getDailySchedule;
    if (checkTemplate.includes(targetName)) {
      setCheckTemplate(checkTemplate.filter(item => item !== targetName));
    } else {
      setCheckTemplate([...checkTemplate, targetName]);
    }
  };

  /**
   * CSVファイル選択時処理
   * @param csv csvファイルデータ
   */
  const uploadCSVSelect = (csv: File) => {
    // ファイル読み込み
    const reader = new FileReader();
    reader.readAsText(csv);
    reader.onload = () => {
      const readData: string = reader.result as string;
      setDiffButtonDisabled(false);
      // データチェック
      let returnData = updateScheduleCSVData(readData);
      // データ不正の場合はダイアログでエラー内容を表示
      if (returnData['errorCode'] !== '') {
        setCSVFormatError(returnData);
        setIsOpenCsvFormatErrorDialog(true);
        setDiffButtonDisabled(true);
        return;
      }
      // 登録API用に値を保存
      setCSVData(returnData['processedData']);
    };
  };

  /**
   * UPLOADボタン押下時処理
   */
  const handleClickUpload = () => {
    setAPIWait(true);
    if (siteID === undefined) return;
    if (csvData === undefined) return;
    //Bodyデータの初期値を作成
    let copyData: apiIF.reqParameters.patchDailySchedule = {};
    //prettier-ignore
    //特定日４つをループ
    for (const key of Object.keys(dailyScheduleDataInitial) as (keyof apiIF.res.getDailySchedule)[]){
      //チェックボックスで選択されていない特定日のスケジュールはBodyデータから削除する
      if (checkTemplate.includes(key) === false) {
        continue;
      }
      //選択された特定日のデータをCSVデータに置き換える
      copyData[key] = {schedule : csvData};
    }
    patchDailySchedule(siteID, props.targetGroupID, copyData, {format: 'csv'})
      .then(response => {
        //スケジュール設定更新
        props.scheduleChangeHandler();
        handleClose();
      })
      .catch(err => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setDiffButtonDisabled(true);
    setCheckTemplate([]);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('UploadFileDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__copyDialog__content">
          <Stack spacing={2}>
            {/** グループの名前 */}
            <Box>{props.targetGroupName}</Box>
            {/** ファイル選択ボタン */}
            <Box className="schedule__copyDialog__content__fileSelectArea">
              <CSVUploadButton
                id="mv-ConfigCSVSelect"
                setCSVFunc={uploadCSVSelect}
              />
            </Box>
            {/** コピー先の特定日の種類選択欄 */}
            <Box className="schedule__copyDialog__content__specificScheduleCheckArea">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={2}>
                  {trans('UploadFileDialog.to')}
                </Grid>
                <Grid item xs={10}>
                  <FormControl>
                    <FormGroup>
                      {tempType.map(info => (
                        <FormControlLabel
                          key={`selectType-${info.name}`}
                          control={
                            <Checkbox
                              checked={checkTemplate.includes(info.name)}
                              onChange={handleChangeType}
                              name={info.name}
                            />
                          }
                          label={info.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('UploadFileDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={handleClickUpload}
            disabled={checkTemplate.length === 0 || diffButtonDisabled}
            apiWait={apiWait}
            buttonText={trans('UploadFileDialog.buttonTxt.upload')}
          />
        </DialogActions>
      </Dialog>
      <CSVFormatErrorMessage
        open={isOpenCsvFormatErrorDialog}
        setOpen={setIsOpenCsvFormatErrorDialog}
        errorMessage={csvFormatError}
        id="mv-btnErrAddGw"
      />
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
export default UploadFileDialog;
